import { InferGetStaticPropsType } from "next";
import {
  LandingPage as LandingPageComponent,
  LandingPageProps,
} from "~/components/LandingPage/LandingPage";

import {
  LandingPageV2 as LandingPageV2Component,
  LandingPageV2Props,
} from "~/components/LandingPageV2/LandingPage";

import { apiToLandingPageProps } from "~/components/LandingPage/apiToLandingPage";
import { Layout } from "~/components/Layout";
import { PageMeta, PostMetadata } from "~/components/PageMeta";
import { apiLandingPageToPageMeta } from "~/components/PageMeta/PageMeta";
import { apiToPostEditCTAProps } from "~/components/PostEditCTA/apiToPostEditCTAProps";
import {
  fetchLandingPage,
  fetchLandingPagePreview,
  fetchLandingPageV2,
  fetchLandingPageV2Preview,
} from "~/lib/server/asgardApiCalls";
import { createGetStaticPathsForPosts } from "~/lib/server/createGetStaticPathsForPosts";
import { wrapGetStaticProps } from "~/lib/server/wrapGetProps";
import { PostTypeEnum } from "~/types/asgard/PostType";
import { NextPageWithLayout } from "~/types/endil/NextPageWithLayout";
import { apiToLandingPageV2Props } from "~/components/LandingPageV2/apiToLandingPage";
import { logger } from "~/common/logger";

export const getStaticPaths = createGetStaticPathsForPosts(
  PostTypeEnum.LandingPage,
  PostTypeEnum.LandingPage,
  "slug"
);

type BaseProps = {
  meta: PostMetadata;
  layout: {
    hideFooter: boolean;
  };
};

type LandingPagePropsWithMeta = BaseProps & {
  page: LandingPageProps;
  version: "v1";
};

type LandingPageV2PropsWithMeta = BaseProps & {
  page: LandingPageV2Props;
  version: "v2";
};

type LandingPageParams = {
  slug: string;
};

export const getStaticProps = wrapGetStaticProps<
  LandingPagePropsWithMeta | LandingPageV2PropsWithMeta,
  LandingPageParams
>(async ({ params, preview }) => {
  const { slug } = params!;
  const fetchMethodV2 = preview
    ? fetchLandingPageV2Preview
    : fetchLandingPageV2;

  const fetchMethod = preview ? fetchLandingPagePreview : fetchLandingPage;

  const getEditCTAProps = (postId: number) =>
    apiToPostEditCTAProps({
      isPreview: preview,
      postId,
      postParam: slug,
    });

  try {
    const { data: pageV2 } = await fetchMethodV2(slug);

    if (pageV2) {
      return {
        props: {
          page: {
            ...apiToLandingPageV2Props(pageV2, !!preview),
            preview: !!preview,
            postEditCTAProps: getEditCTAProps(pageV2.id),
          },
          version: "v2",
          meta: apiLandingPageToPageMeta(pageV2),
          layout: {
            hideFooter: !!pageV2.hideFooter,
          },
        } satisfies LandingPageV2PropsWithMeta,
      };
    }
  } catch (err) {
    logger.info(`V2 Landing Page page for slug '${slug}' not found`);
  }

  const { data: page } = await fetchMethod(slug);

  return {
    props: {
      page: {
        ...apiToLandingPageProps(page, !!preview),
        postEditCTAProps: await getEditCTAProps(page.id),
      },
      version: "v1",
      meta: apiLandingPageToPageMeta(page),
      layout: {
        hideFooter: !!page.hideFooter,
      },
    } satisfies LandingPagePropsWithMeta,
  };
});

const LandingPage: NextPageWithLayout<
  InferGetStaticPropsType<typeof getStaticProps>
> = ({ meta, page, version }) => (
  <>
    <PageMeta {...meta} />
    {version === "v2" ? (
      <LandingPageV2Component {...page} />
    ) : (
      <LandingPageComponent {...page} />
    )}
  </>
);

LandingPage.getLayout = (page, { pageProps }) => {
  const {
    layout: { hideFooter },
  } = pageProps as LandingPagePropsWithMeta;
  return <Layout hideFooter={hideFooter}>{page}</Layout>;
};

export default LandingPage;
